import { GLOBAL_CONSTANTS } from "../../../constants/constants"
import { formatDate, formatCurrency } from "../../../helpers/helpers"
import { Table, Typography, App } from "antd"
import CustomButton from "../../../components/CustomButton/Button";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getAgreementEsignStatus } from "../../../redux/slices/EsignSlice"
import { dealDetailedView } from "../../../redux/slices/dealsSlice"

const { Text } = Typography;

const PENDING_SIGN_KEY = "adminDealAgreementSignPending";

const DealLevelFunding = ({ deal, status, dealId }) => {
    const { message } = App.useApp();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [locallySigned, setLocallySigned] = useState(() => new Set());

    const markSigned = useCallback((transactionId) => {
        setLocallySigned((prev) => {
            const next = new Set(prev);
            if (transactionId) next.add(transactionId);
            else next.add(dealId || "__deal__");
            return next;
        });
    }, [dealId]);

    const isRowSigned = useCallback((row) => {
        if (row?.signStatus === true) return true;
        if (row?.transactionId && locallySigned.has(row.transactionId)) return true;
        if (dealId && locallySigned.has(dealId)) return true;
        return locallySigned.has("__deal__");
    }, [locallySigned, dealId]);

    const checkPendingSignStatus = useCallback(async () => {
        const stored = localStorage.getItem(PENDING_SIGN_KEY);
        if (!stored) return;

        let pending;
        try {
            pending = JSON.parse(stored);
        } catch {
            localStorage.removeItem(PENDING_SIGN_KEY);
            return;
        }

        if (pending?.dealId && dealId && pending.dealId !== dealId) return;

        setLoading(true);
        try {
            const res = await dispatch(getAgreementEsignStatus({
                transactionId: pending.transactionId,
                dealId: pending.dealId || dealId,
            }));

            if (res.meta?.requestStatus === "rejected") return;

            if (res.payload?.response?.status === "signed") {
                markSigned(pending.transactionId);
                localStorage.removeItem(PENDING_SIGN_KEY);
                if (dealId) dispatch(dealDetailedView(dealId));
            }
        } finally {
            setLoading(false);
        }
    }, [dispatch, dealId, markSigned]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                checkPendingSignStatus();
            }
        };
        const onFocus = () => checkPendingSignStatus();

        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onFocus);
        checkPendingSignStatus();

        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onFocus);
        };
    }, [checkPendingSignStatus]);

    const handleSignAgreement = useCallback(async (transactionId, link) => {
        setLoading(true);

        try {
            const res = await dispatch(getAgreementEsignStatus({ transactionId, dealId }));

            if (res.meta?.requestStatus === "rejected") {
                message.error(res.payload?.error || res.payload?.message || "Failed to check e-sign status.");
                return;
            }

            if (res.payload?.response?.status === "signed") {
                markSigned(transactionId);
                localStorage.removeItem(PENDING_SIGN_KEY);
                if (dealId) dispatch(dealDetailedView(dealId));
                alert("E-sign is already completed.");
            } else if (link) {
                localStorage.setItem(
                    PENDING_SIGN_KEY,
                    JSON.stringify({ transactionId: transactionId || null, dealId })
                );
                window.open(link, "_blank");
            }
        } catch (error) {
            message.error("Error checking e-sign status:", error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, dealId, markSigned, message]);

    const columns = useMemo(() => [
        {
            title: "Transaction Id",
            dataIndex: "transactionId",
            key: "transactionId",
            width: 200,
            render: (text) => <Text style={{ fontSize: 14 }}>{text || GLOBAL_CONSTANTS.NA}</Text>,
        },
        {
            title: "Transaction Date",
            dataIndex: "transactionDate",
            key: "transactionDate",
            width: 150,
            render: (text) => <Text style={{ fontSize: 14 }}>{formatDate(text)}</Text>,
        },
        {
            title: "UTR",
            dataIndex: "utr",
            key: "utr",
            width: 180,
            render: (text) => <Text style={{ fontSize: 14 }}>{text || GLOBAL_CONSTANTS.NA}</Text>,
        },
        {
            title: "Funded Amount",
            dataIndex: "fundedAmount",
            key: "fundedAmount",
            width: 150,
            render: (text) => <Text style={{ fontSize: 14 }}>{formatCurrency(text || 0)}</Text>,
        },
        {
            title: "Interest Amount",
            dataIndex: "discountAmt",
            key: "discountAmt",
            width: 150,
            render: (text) => <Text style={{ fontSize: 14 }}>{formatCurrency(text || 0)}</Text>,
        },
        {
            title: "Disbursed Amount",
            dataIndex: "disbursedAmount",
            key: "disbursedAmount",
            width: 180,
            render: (text) => <Text style={{ fontSize: 14 }}>{formatCurrency(text || 0)}</Text>,
        },
        {
            title: "Sanction Fee / Withheld Amount",
            dataIndex: "withheldAmount",
            key: "withheldAmount",
            width: 250,
            render: (text) => <Text style={{ fontSize: 14 }}>{formatCurrency(text || 0)}</Text>,
        },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            render: (_, row) => {
                const link = row?.link;
                const transactionId = row?.transactionId;
                const signed = isRowSigned(row);
                return (
                    <>
                        {status === "agreement" && (
                            <CustomButton
                                disabled={signed}
                                onClick={() => handleSignAgreement(transactionId, link)}
                            >
                                {signed ? "Agreement Signed" : loading ? "Checking..." : "Sign Agreement"}
                            </CustomButton>
                        )}
                    </>
                );
            },
        },
    ], [status, loading, handleSignAgreement, isRowSigned]);

    return (
        <Table
            columns={columns}
            dataSource={deal}
            bordered
            scroll={{ x: "max-content" }}
            pagination={{ position: ["bottomCenter"] }}
            className="deal-level-funding-table"
        />
    );
};

export default DealLevelFunding;
